import { useEffect, useRef } from 'react';
import { UseCanvasStateReturn } from './useCanvasState';
import { CreateMode } from '../../editing/types';

export function useKeyboardShortcuts(
  state: UseCanvasStateReturn,
  handleDelete: (ids: string[]) => void
) {
  const {
    mode,
    selectedIds,
    elements,
    connectorMode,
    setConnectorMode,
    createMode,
    setCreateMode,
    historyManagerRef,
    clipboardManagerRef,
    groupingManagerRef,
    connectorManagerRef,
  } = state;

  // Store previous mode when Space is held for temporary pan mode
  const previousModeRef = useRef<CreateMode | null>(null);
  const isSpaceHeldRef = useRef<boolean>(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (mode !== 'edit') return;
      
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable) return;
      
      // Don't trigger tool shortcuts if modifier keys are held (for copy/paste/etc)
      const hasModifier = e.ctrlKey || e.metaKey || e.altKey || e.shiftKey;
      
      // Undo/Redo
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          historyManagerRef.current?.redo();
        } else {
          historyManagerRef.current?.undo();
        }
        return;
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        historyManagerRef.current?.redo();
        return;
      }
      
      // Copy/Paste/Cut/Duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        clipboardManagerRef.current?.copy(elements, selectedIds);
        return;
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        // Don't preventDefault - let paste event fire
        // The paste event handler will check for images first
        // If no images are found, it will allow canvas element paste to proceed
        // This allows both image paste and canvas element paste to work
        return;
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        e.preventDefault();
        clipboardManagerRef.current?.cut(elements, selectedIds);
        return;
      }
      
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        e.preventDefault();
        clipboardManagerRef.current?.duplicate(elements, selectedIds);
        return;
      }
      
      // Grouping
      if ((e.ctrlKey || e.metaKey) && e.key === 'g') {
        e.preventDefault();
        if (e.shiftKey) {
          // Ungroup
          if (selectedIds.length === 1) {
            groupingManagerRef.current?.ungroup(elements, selectedIds[0]);
          }
        } else {
          // Group
          if (selectedIds.length >= 2) {
            groupingManagerRef.current?.group(elements, selectedIds);
          }
        }
        return;
      }
      
      // Delete
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedIds.length > 0) {
          e.preventDefault();
          handleDelete(selectedIds);
        }
        return;
      }
      
      // Connector shortcuts
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        if (e.shiftKey) {
          setConnectorMode('bent');
        } else {
          setConnectorMode('straight');
        }
        return;
      }
      
      if (e.key === 'x' || e.key === 'X') {
        e.preventDefault();
        setConnectorMode('bent');
        return;
      }
      
      // Tool switching shortcuts (only when no modifiers)
      if (!hasModifier) {
        // V - Select tool (arrow)
        if (e.key === 'v' || e.key === 'V') {
          e.preventDefault();
          if (connectorMode) {
            setConnectorMode(null);
            connectorManagerRef.current?.cancel();
          }
          setCreateMode({ type: 'select' });
          return;
        }
        
        // T - Text tool
        if (e.key === 't' || e.key === 'T') {
          e.preventDefault();
          setCreateMode({ type: 'text' });
          return;
        }
        
        // R - Rectangle shape
        if (e.key === 'r' || e.key === 'R') {
          e.preventDefault();
          setCreateMode({ type: 'shape', shapeType: 'rectangle-rounded' });
          return;
        }
        
        // O - Circle shape
        if (e.key === 'o' || e.key === 'O') {
          e.preventDefault();
          setCreateMode({ type: 'shape', shapeType: 'circle' });
          return;
        }
        
        // Space - Hold to temporarily enable pan mode
        if (e.key === ' ' || e.key === 'Space') {
          e.preventDefault(); // Prevent page scrolling
          if (!isSpaceHeldRef.current) {
            isSpaceHeldRef.current = true;
            previousModeRef.current = createMode;
            setCreateMode({ type: 'pan' });
          }
          return;
        }
      }
      
      // Escape to exit modes
      if (e.key === 'Escape') {
        if (connectorMode) {
          e.preventDefault();
          setConnectorMode(null);
          connectorManagerRef.current?.cancel();
        }
        if (createMode.type !== 'select') {
          e.preventDefault();
          setCreateMode({ type: 'select' });
        }
        return;
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (mode !== 'edit') return;
      
      // Space key released - restore previous mode
      if ((e.key === ' ' || e.key === 'Space') && isSpaceHeldRef.current) {
        e.preventDefault();
        isSpaceHeldRef.current = false;
        if (previousModeRef.current) {
          setCreateMode(previousModeRef.current);
          previousModeRef.current = null;
        } else {
          setCreateMode({ type: 'select' });
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [mode, selectedIds, elements, handleDelete, connectorMode, createMode, setConnectorMode, setCreateMode, historyManagerRef, clipboardManagerRef, groupingManagerRef, connectorManagerRef]);
}
